/**
 * GradientText - Cross-platform gradient text component
 *
 * Phase 2: Native gradient support using MaskedView + expo-linear-gradient
 *
 * Platform Implementation:
 * - Web: Uses .text-gradient-brand CSS class from global.css
 * - iOS/Android: Uses MaskedView + LinearGradient for true native gradient
 *
 * Gradient Colors:
 * - Purple to Blue: #A78BFA → #60A5FA
 * - Matches develop branch brand styling
 *
 * Usage:
 * ```tsx
 * <GradientText style={styles.brandText}>
 *   DramaDetector
 * </GradientText>
 * ```
 */

import type { ReactNode } from 'react';
import { useState } from 'react';
import { Platform, Text, type TextProps, type StyleProp, type TextStyle } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import MaskedView from '@react-native-masked-view/masked-view';

export interface GradientTextProps extends TextProps {
  /** Text content */
  children: ReactNode;
  /** Text styles (applied to both mask and display text) */
  style?: StyleProp<TextStyle>;
  /** Gradient colors (defaults to brand purple→blue) */
  colors?: [string, string];
}

/**
 * GradientText Component
 *
 * Renders gradient text with platform-specific implementations:
 * - Web: CSS gradient text (background-clip: text)
 * - Native: MaskedView + LinearGradient composition
 */
export function GradientText({
  children,
  style,
  colors = ['#A78BFA', '#60A5FA'], // Purple → Blue (brand gradient)
  ...textProps
}: GradientTextProps) {
  // Track text dimensions for native gradient sizing
  const [textDimensions, setTextDimensions] = useState<{ width: number; height: number } | null>(null);

  // Web: Use CSS class for gradient text
  if (Platform.OS === 'web') {
    // Remove 'color' from style to prevent inline style from overriding CSS gradient
    // The CSS class .text-gradient-brand uses -webkit-text-fill-color: transparent
    // which requires no competing color property
    const styleWithoutColor = (() => {
      if (!style) return undefined;
      if (Array.isArray(style)) {
        return style.map(s => {
          if (!s || typeof s !== 'object') return s;
          const { color, ...rest } = s as Record<string, any>;
          return rest;
        });
      }
      if (typeof style === 'object') {
        const { color, ...rest } = style as Record<string, any>;
        return rest;
      }
      return style;
    })();

    return (
      <Text
        {...textProps}
        style={styleWithoutColor}
        className="text-gradient-brand"
      >
        {children}
      </Text>
    );
  }

  // Native: Use MaskedView + LinearGradient with explicit sizing
  // Measure text first to prevent blank rendering when gradient has no intrinsic size
  return (
    <MaskedView
      maskElement={
        <Text {...textProps} style={style}>
          {children}
        </Text>
      }
    >
      <LinearGradient
        colors={colors}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 0 }}
        style={textDimensions ? { width: textDimensions.width, height: textDimensions.height } : undefined}
      >
        {/* Invisible text for size calculation */}
        <Text
          {...textProps}
          style={[style, { opacity: 0 }]}
          onLayout={(e) => {
            const { width, height } = e.nativeEvent.layout;

            // Update dimensions once measured
            if (width > 0 && height > 0 && !textDimensions) {
              setTextDimensions({ width, height });
            }

            // DEV WARNING: Detect potential blank text rendering
            if (__DEV__ && width === 0 && height === 0) {
              console.warn(
                '[GradientText] Detected 0×0 layout - text may render blank. ' +
                'Ensure parent provides size constraints or text has intrinsic size (fontSize, etc.).'
              );
            }
          }}
        >
          {children}
        </Text>
      </LinearGradient>
    </MaskedView>
  );
}
